import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Página intermediária do fluxo Azure AD SSO.
 * Recebe o JWT BlueGov via hash fragment (#token=...&redirect=/dashboard),
 * persiste no AuthContext usando o mesmo padrão do login local e redireciona.
 */
function readHashPayload() {
  const hash = window.location.hash || '';
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : '');
  const token = params.get('token');
  const redirectRaw = params.get('redirect') || '/dashboard';
  const redirect = redirectRaw.startsWith('/') && !redirectRaw.startsWith('//') ? redirectRaw : '/dashboard';
  return { token, redirect };
}

export default function AzureSuccessPage() {
  const navigate = useNavigate();
  const { setAuthFromExternalToken } = useAuth();
  // Lazy state initializer — lê o hash uma única vez na primeira renderização.
  const [payload] = React.useState(readHashPayload);
  const [error, setError] = React.useState(payload.token ? null : 'Token ausente na resposta do SSO');

  useEffect(() => {
    if (!payload.token) return;
    // Limpa o hash antes de qualquer navegação para não vazar token em histórico
    window.history.replaceState({}, '', window.location.pathname);
    setAuthFromExternalToken(payload.token)
      .then(() => {
        toast.success('Login Microsoft concluído');
        navigate(payload.redirect, { replace: true });
      })
      .catch((err) => {
        setError(err?.message || 'Falha ao validar token Microsoft');
      });
  }, [navigate, setAuthFromExternalToken, payload.token, payload.redirect]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-sm w-full text-center" data-testid="azure-success-page">
        {error ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">SSO Microsoft falhou</h1>
            <p className="text-sm text-slate-600 mb-4" data-testid="azure-success-error">{error}</p>
            <button
              className="text-sm text-blue-700 hover:underline"
              onClick={() => navigate('/login', { replace: true })}
              data-testid="azure-success-back-btn"
            >
              Voltar ao login
            </button>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <ShieldCheck className="w-7 h-7 text-blue-700" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Concluindo autenticação…</h1>
            <p className="text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Validando sessão Microsoft
            </p>
          </>
        )}
      </div>
    </div>
  );
}
